import React from 'react';
import { faCheck, faChevronDown } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MutedText } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { FormFieldLabel } from './form-field-label';
import { FormFieldMessages } from './form-field-messages';
import type { BaseFormFieldProps } from './shared';
import { useFormField } from './shared';

type SelectOption = {
  value: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  keywords?: string[];
  disabled?: boolean;
};

type SelectInputProps = BaseFormFieldProps & {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  contentClassName?: string;
  valueContent?: (option: SelectOption | undefined) => React.ReactNode;
  searchable?: boolean;
  wrapDescriptions?: boolean;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  shouldFilter?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
};

function normalizeSearchValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_/+:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function compactSearchValue(value: string): string {
  return normalizeSearchValue(value).replace(/[^a-z0-9]/g, '');
}

function filterSelectOption(value: string, search: string, keywords?: string[]): number {
  const query = normalizeSearchValue(search);
  if (!query) return 1;

  const compactQuery = compactSearchValue(search);
  const candidates = [value, ...(keywords ?? [])];

  return candidates.some((candidate) => {
    const normalized = normalizeSearchValue(candidate);
    const compact = compactSearchValue(candidate);

    return (
      normalized.includes(query) || (compactQuery.length > 0 && compact.includes(compactQuery))
    );
  })
    ? 1
    : 0;
}

export function SelectInput({
  id,
  label,
  helperText,
  helperTextPlacement = 'beforeControl',
  errorText,
  isRequired,
  showAsterisk,
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No option found.',
  className,
  contentClassName,
  valueContent,
  searchable = true,
  wrapDescriptions = false,
  searchValue,
  onSearchValueChange,
  shouldFilter,
  fullWidth = true,
  disabled,
  size = 'md',
  invalid,
}: SelectInputProps) {
  const { fieldId, helperId, errorId, describedBy } = useFormField(id, helperText, errorText);
  const [open, setOpen] = React.useState(false);
  const [internalSearchValue, setInternalSearchValue] = React.useState('');
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const isInvalid = Boolean(invalid || errorText);
  const isSearchControlled = searchValue !== undefined && onSearchValueChange !== undefined;
  const currentSearchValue = searchValue ?? internalSearchValue;

  const selectedValue = value ?? defaultValue ?? '';
  const selectedOption = options.find((opt) => opt.value === selectedValue);
  const selectedIndex = options.findIndex((option) => option.value === selectedValue);

  const clearSearchValue = React.useCallback(() => {
    if (!currentSearchValue) return;

    if (isSearchControlled) {
      onSearchValueChange('');
      return;
    }

    setInternalSearchValue('');
  }, [currentSearchValue, isSearchControlled, onSearchValueChange]);

  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      if (onValueChange) {
        onValueChange(selectedValue);
      }
      clearSearchValue();
      setOpen(false);
    },
    [clearSearchValue, onValueChange],
  );

  const renderValue = () => {
    if (valueContent) {
      return valueContent(selectedOption);
    }
    return selectedOption ? selectedOption.title : placeholder;
  };

  const getSearchableKeywords = (option: SelectOption) => {
    const titleValue =
      typeof option.title === 'string' || typeof option.title === 'number'
        ? String(option.title)
        : option.value;
    const keywords = [titleValue, option.value.replace(/_/g, ' '), ...(option.keywords ?? [])];

    return keywords.flatMap((keyword) => [keyword, keyword.replace(/[_/]/g, ' ')]);
  };

  const scrollSelectedOptionIntoView = React.useCallback(() => {
    const scrollList = () => {
      const list = listRef.current;
      if (!list || selectedIndex < 0) return;

      const selectedItem = list.querySelector<HTMLElement>('[data-select-input-selected="true"]');

      if (selectedItem) {
        const targetScrollTop = Math.max(
          selectedItem.offsetTop - list.clientHeight / 2 + selectedItem.offsetHeight / 2,
          0,
        );

        list.scrollTop = targetScrollTop;
        return;
      }

      const estimatedOptionHeight = 44;
      const scrollPadding = estimatedOptionHeight * 2;
      list.scrollTop = Math.max(selectedIndex * estimatedOptionHeight - scrollPadding, 0);
    };

    scrollList();
    requestAnimationFrame(scrollList);
    setTimeout(scrollList, 0);
  }, [selectedIndex]);

  React.useEffect(() => {
    if (!open) return;

    scrollSelectedOptionIntoView();
  }, [open, scrollSelectedOptionIntoView]);

  const setListElement = React.useCallback(
    (list: HTMLDivElement | null) => {
      listRef.current = list;
      if (!list) return;

      scrollSelectedOptionIntoView();
    },
    [scrollSelectedOptionIntoView],
  );

  React.useEffect(() => {
    if (!open || !currentSearchValue) return;

    const resetScroll = () => {
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    };

    // Reset immediately and on the next frame; cmdk applies filtering after input state updates.
    resetScroll();
    requestAnimationFrame(resetScroll);
  }, [currentSearchValue, open]);

  const handleSearchValueChange = React.useCallback(
    (nextValue: string) => {
      if (isSearchControlled) {
        onSearchValueChange(nextValue);
        return;
      }

      setInternalSearchValue(nextValue);
    },
    [isSearchControlled, onSearchValueChange],
  );

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen || !currentSearchValue) return;

      clearSearchValue();
    },
    [clearSearchValue, currentSearchValue],
  );

  return (
    <div className={cn('grid gap-1.5', fullWidth && 'w-full', className)}>
      {label && (
        <FormFieldLabel
          htmlFor={fieldId}
          label={label}
          isRequired={isRequired}
          showAsterisk={showAsterisk}
        />
      )}
      {helperText && helperTextPlacement === 'beforeControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id={fieldId}
            variant="outline"
            colorScheme="secondary"
            size={size}
            role="combobox"
            aria-expanded={open}
            aria-required={isRequired}
            aria-invalid={isInvalid || undefined}
            aria-describedby={describedBy}
            disabled={disabled}
            className={cn(
              fullWidth && 'w-full',
              'justify-between text-left font-normal hover:bg-primary-foreground min-w-0',
              open && 'ring-1 ring-ring/30',
              !selectedOption && 'text-muted-foreground',
              isInvalid && 'border-destructive',
            )}
          >
            <span className="truncate min-w-0 flex-1 text-left overflow-hidden">
              {renderValue()}
            </span>
            <Icon icon={faChevronDown} className="ml-2 shrink-0 opacity-50 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('w-(--radix-popover-trigger-width)', 'p-0', contentClassName)}
          align="start"
        >
          <Command shouldFilter={shouldFilter} filter={filterSelectOption}>
            {searchable && (
              <CommandInput
                placeholder={searchPlaceholder}
                value={currentSearchValue}
                onValueChange={handleSearchValueChange}
              />
            )}
            <div
              ref={setListElement}
              data-testid="select-input-scroll"
              className="max-h-[300px] overflow-y-auto"
            >
              <CommandList className="max-h-none overflow-visible">
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      keywords={getSearchableKeywords(option)}
                      data-select-input-selected={option.value === selectedValue}
                      onSelect={() => !option.disabled && handleSelect(option.value)}
                      disabled={option.disabled}
                      className={cn(
                        'flex items-start gap-3 hover:bg-accent hover:text-accent-foreground',
                        option.value === selectedValue && 'bg-accent text-accent-foreground',
                      )}
                    >
                      {option.icon && <div className="mt-0.5 shrink-0">{option.icon}</div>}
                      <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
                        <div className="line-clamp-1 text-sm font-medium">{option.title}</div>
                        {option.description && (
                          <MutedText
                            className={cn(
                              'text-xs',
                              wrapDescriptions ? 'whitespace-normal' : 'line-clamp-1',
                            )}
                          >
                            {option.description}
                          </MutedText>
                        )}
                      </div>
                      {option.value === selectedValue && (
                        <Icon icon={faCheck} className="ml-auto shrink-0" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
      {helperText && helperTextPlacement === 'afterControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      {errorText && <FormFieldMessages errorText={errorText} errorId={errorId} />}
    </div>
  );
}
